<template>
  <div class="top-sidebar-container">
    <div class="top-sidebar">
      <img
        v-if="domain.includes('dvgroup.com')"
        src="@/assets/images/logo_black.svg"
        alt="logo"
      />
      <div v-else></div>

      <div v-if="isOrgMayChanged" class="flex fl-1 fl-jc-end m-r-20">
        <Dropdown
          v-model="org"
          class="org-dropdown"
          :options="orgStore.itemList"
          :loading="isLoading"
          filter
          panelClass="org-drop"
          optionLabel="name"
          @change="onChangeOrg"
          placeholder="Загрузка..."
        />
      </div>

      <div class="user" v-if="store.user" @click="toggle">
        <Avatar icon="pi pi-user" class="dv-avatar" shape="circle" />
        <div class="user-info flex flex-col">
          <div class="user-name">{{ getUserName(store.user) }}</div>
          <div class="user-agency" v-if="store.user.role">
            {{ store.user.role.department.organization.name }}
          </div>
        </div>
      </div>
    </div>

    <OverlayPanel ref="op" showCloseIcon>
      <div v-if="store.user" class="user-panel">
        <div class="user-name">{{ getUserName(store.user) }}</div>
        <div class="user-agency m-t-4" v-if="store.user.role">
          {{ store.user.role.department.organization.name }}
        </div>
        <div class="user-email m-t-4">{{ store.user.email }}</div>
        <div class="user-department m-t-12" v-if="store.user.role">
          {{ store.user.role.department.name }} / {{ store.user.role.name }}
        </div>
        <div class="user-panel-actions">
          <Button
            label="Личный кабинет"
            class="panel-link-button profile-button"
            icon="pi pi-id-card"
            @click="goToPersonalAccount"
          />

          <Button
            label="Выйти из аккаунта"
            class="panel-link-button logout-button"
            icon="pi pi-sign-out"
            @click="logout"
          />
        </div>
      </div>
    </OverlayPanel>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";

import { useAuthStore } from "@/store/auth/login";
import { useOrganizationsBaseDictionaryStore } from "@/store/catalog_data/organization_base";

import Avatar from "primevue/avatar";
import Button from "primevue/button";
import OverlayPanel from "primevue/overlaypanel";
import Dropdown from "primevue/dropdown";

import { getUserName } from "@/services/utils/Names";
import { getCurrentDomain } from "@/services/utils/Domain";

const domain = getCurrentDomain();
const router = useRouter();

const store = useAuthStore();
const orgStore = useOrganizationsBaseDictionaryStore();

const op = ref();
const isLoading = ref(false);
const org = ref(null);

const isOrgMayChanged = computed(() => {
  return !store.isDeniedForPermissions([
    "external_agency:read",
    "external_agency:edit",
  ]);
});

onMounted(() => {
  if (!store.user && store.isLoggedIn) {
    store.getUserInfo();
  }

  isLoading.value = true;

  orgStore.getItems().then(() => {
    store.getOrganizationByUserTenant().then((orgValue: any) => {
      if (orgValue) {
        org.value = orgValue;
      }
      isLoading.value = false;
    });
  });
});

const toggle = (event: any) => {
  op.value.toggle(event);
};

const onChangeOrg = (event: any) => {
  store.changeTenant(event.value.tenant);
};

const goToPersonalAccount = () => {
  op.value.hide();
  router.push({ name: "personalAccount" });
};

function logout() {
  store.logout().then(() => {
    location.reload();
  });
}
</script>

<style lang="scss" scoped>
.top-sidebar-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  padding: 12px;
  background-color: $page-background-color;

  z-index: 1000;
}

.org-dropdown.p-dropdown {
  border: none;
  background-color: #f7f7fd;

  .p-dropdown-label {
    padding: 0;
  }
}

.top-sidebar {
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: 48px;

  border-radius: 8px;
  background-color: white;

  padding: 8px 16px;

  img {
    height: 17px;
  }
}

.dv-avatar {
  color: white;
  background-color: #6366f1;
  margin-right: 8px;
  width: 30px;
  height: 30px;
}

.user {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.user-name {
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
}

.user-agency {
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.2px;
}

.user-panel {
  min-width: 240px;
  padding: 3px;
}

.user-email {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  color: #a19cab;
}

.user-department {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  padding: 2px 4px;
  border-radius: 4px;
  width: fit-content;
  background-color: #eaeaf0;
}

.user-panel-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 16px;
  padding-top: 6px;
  border-top: 1px solid #eaeaf0;
}

.panel-action-divider {
  border-top: 1px solid #eaeaf0;
  margin: 4px 0;
}

.panel-link-button {
  width: 100%;
  justify-content: flex-start;
  background: none;
  border: none;
  color: #2b2a5c;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f1eff3;
  }

  &:focus {
    box-shadow: none;
  }
}

:deep(.profile-button.p-button),
:deep(.logout-button.p-button) {
  justify-content: flex-start !important;
}

:deep(.profile-button .p-button-label),
:deep(.logout-button .p-button-label) {
  flex: initial;
  text-align: left;
}
</style>
